import React, { useEffect, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/es/table/interface";
import moment from "moment";

import { createFarmDocumentScanFromDocument, getDocScanStatistics, listFarmDocumentScans } from "services";

import { copyToClipboard, hashFile, useLocation, useNavigate } from "utils";

import {
  DEFAULT_SHORT_CACHE,
  DOC_SCAN_MAX_FILE_SIZE_BYTES,
  DOC_SCAN_MAX_FILE_SIZE_MB,
  DOC_SCAN_REFRESH_INTERVAL,
  GET_DOC_SCANS,
  GET_DOC_SCAN_STATISTICS,
} from "consts";

import { DocScanJob, DocScanSortBy, DocScanSortOrder, DocScanType } from "models";

import {
  AdminHead,
  AlertBanner,
  Button,
  Col,
  Flex,
  Form,
  Icon,
  Input,
  MainPage,
  Modal,
  Option,
  Row,
  Select,
  Table,
  TagNormal,
  TooltipWrapper,
  Typography,
  UploadFile,
  Message as message,
} from "components";

import { DocScanSourceTag, DocScanTriggeredBy } from "pages/admin/verifications/DocScanOrigin";
import { DocScanStatisticsCard } from "pages/admin/verifications/DocScanStatisticsCard";

const STATUS_FIELD_COLORS: Record<string, string> = {
  match: "green",
  matches: "green",
  mismatch: "red",
  mismatches: "red",
  new_data: "indigo",
  new: "indigo",
};

const getFieldStatusColor = (status: string | undefined | null) => STATUS_FIELD_COLORS[status ?? ""] ?? "grey-600";

const STATUS_COLORS: Record<string, string> = {
  processing: "blue",
  completed: "green",
  failed: "red",
  pending: "yellow",
};

const TYPE_LABELS: Record<DocScanType, string> = {
  extract: "Extract",
  match: "Match",
};

// const TYPE_COLORS: Record<string, string> = {
//   extract: "purple",
//   match: "blue",
// };

const formatDuration = (diffMs: number): string => {
  if (diffMs <= 0) return "-";

  const duration = moment.duration(diffMs);
  const days = Math.floor(duration.asDays());
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds) parts.push(`${seconds}s`);

  return parts.length ? parts.join(" ") : "-";
};

const defaultLimit = 10;

export const DocScanList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [sortBy, setSortBy] = useState<DocScanSortBy>("completedAt");
  const [sortOrder, setSortOrder] = useState<DocScanSortOrder>("desc");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [docScanFile, setDocScanFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<{
    success: boolean;
    uploading: boolean;
    filename?: string;
    errorMessage?: string;
  }>({
    success: false,
    uploading: false,
    filename: undefined,
    errorMessage: undefined,
  });

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const columnKeyToSortBy: Record<string, DocScanSortBy> = {
    id: "id",
    status: "status",
  };

  const applyUrlState = () => {
    const params = new URLSearchParams(location.search);

    const hasPage = params.has("page");
    const hasLimit = params.has("limit");
    const hasSortBy = params.has("sortBy");
    const hasSortOrder = params.has("sortOrder");

    const pageParam = Number(params.get("page") || "1");
    const limitParam = Number(params.get("limit") || String(defaultLimit));
    const sortByParam = params.get("sortBy") as DocScanSortBy | null;
    const sortOrderParam = params.get("sortOrder") as DocScanSortOrder | null;

    const allowedSortBy: DocScanSortBy[] = ["createdAt", "updatedAt", "completedAt", "status", "id"];
    const allowedSortOrder: DocScanSortOrder[] = ["asc", "desc"];

    const nextPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
    const nextLimit = Number.isFinite(limitParam) && limitParam > 0 ? limitParam : defaultLimit;
    const nextSortBy = sortByParam && allowedSortBy.includes(sortByParam) ? sortByParam : "createdAt";
    const nextSortOrder = sortOrderParam && allowedSortOrder.includes(sortOrderParam) ? sortOrderParam : "desc";

    setPage(nextPage);
    setLimit(nextLimit);
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);

    if (!hasPage || !hasLimit || !hasSortBy || !hasSortOrder) {
      updateUrl(nextPage, nextLimit, nextSortBy, nextSortOrder);
    }
  };

  const updateUrl = (nextPage: number, nextLimit: number, nextSortBy: DocScanSortBy, nextSortOrder: DocScanSortOrder) => {
    const params = new URLSearchParams(location.search);

    params.set("page", String(nextPage));
    params.set("limit", String(nextLimit));
    params.set("sortBy", nextSortBy);
    params.set("sortOrder", nextSortOrder);

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
  };

  useEffect(() => {
    applyUrlState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const { data, isLoading } = useQuery({
    queryKey: [GET_DOC_SCANS, page, limit, sortBy, sortOrder],
    queryFn: () => listFarmDocumentScans({ page, limit, sortBy, sortOrder }),
    ...DEFAULT_SHORT_CACHE,
    refetchInterval: DOC_SCAN_REFRESH_INTERVAL,
  });

  const { data: globalStats } = useQuery({
    queryKey: [GET_DOC_SCAN_STATISTICS],
    queryFn: getDocScanStatistics,
    ...DEFAULT_SHORT_CACHE,
  });

  const handlePageChange = (newPage: number, pageSize: number) => {
    setPage(newPage);
    setLimit(pageSize);
    updateUrl(newPage, pageSize, sortBy, sortOrder);
  };

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TableRow> | SorterResult<TableRow>[],
    _extra: TableCurrentDataSource<TableRow>
  ) => {
    if (Array.isArray(sorter)) return;

    const field = sorter.columnKey as string;
    const mappedSortBy = columnKeyToSortBy[field];

    if (!mappedSortBy) return;

    const nextSortOrder: DocScanSortOrder = sorter.order === "ascend" ? "asc" : "desc";

    setSortBy(mappedSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
    updateUrl(1, limit, mappedSortBy, nextSortOrder);
  };

  type TableRow = {
    key: string;
    id: React.ReactNode;
    status: React.ReactNode;
    type: React.ReactNode;
    source: React.ReactNode;
    year: React.ReactNode;
    overallStatus: React.ReactNode;
    score: React.ReactNode;
    farm: React.ReactNode;
    country: React.ReactNode;
    triggeredBy: React.ReactNode;
    duration: React.ReactNode;
    actions: React.ReactNode;
  };

  const getSortOrder = (columnKey: string) => {
    if (columnKeyToSortBy[columnKey] !== sortBy) return undefined;
    return sortOrder === "asc" ? ("ascend" as const) : ("descend" as const);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: true,
      sortOrder: getSortOrder("id"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: getSortOrder("status"),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
    },
    {
      title: "Overall Status",
      dataIndex: "overallStatus",
      key: "overallStatus",
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
    },
    {
      title: "Farm",
      dataIndex: "farm",
      key: "farm",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "Triggered by",
      dataIndex: "triggeredBy",
      key: "triggeredBy",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
    },
  ];

  const tableData: TableRow[] =
    data?.jobs.map((job: DocScanJob) => {
      const startedAt = job.dispatchedAt ?? job.createdAt;
      const durationContent = job.completedAt && startedAt ? formatDuration(moment(job.completedAt).diff(moment(startedAt))) : null;

      return {
        key: job.id,
        id: (
          <TooltipWrapper tooltip={{ title: job.id, placement: "top" }}>
            <Typography
              variant="paragraph-m"
              className="pointer"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(job.id);
              }}
            >
              <b>
                <small>
                  {job.id.split("-")[0]}... <Icon iconName="tabler/copy" className="green-text pointer inline" size="s" />
                </small>
              </b>
            </Typography>
          </TooltipWrapper>
        ),
        status: (
          <Flex align="center" gap={8}>
            <TagNormal text={job.status} color={STATUS_COLORS[job.status] ?? "grey-600"} />
            {job.dispatchAttempts > 1 && (
              <Typography variant="paragraph-s" className="grey-text" noWrap>
                attempt {job.dispatchAttempts}/{data.maxDispatchAttempts}
              </Typography>
            )}
          </Flex>
        ),
        type: <TagNormal text={TYPE_LABELS[job.type]} color={job.type === "match" ? "blue" : "purple"} />,
        source: <DocScanSourceTag source={job.source} />,
        year:
          job.type === "match" && job.year != null ? (
            <Typography variant="paragraph-m">{job.year}</Typography>
          ) : (
            <Typography variant="paragraph-m" className="grey-text">
              -
            </Typography>
          ),
        overallStatus:
          job.type === "match" && job.overallStatus ? (
            <TagNormal text={job.overallStatus} color={getFieldStatusColor(job.overallStatus)} />
          ) : (
            <Typography variant="paragraph-m" className="grey-text">
              -
            </Typography>
          ),
        score:
          job.type === "match" && job.score != null ? (
            <Typography variant="paragraph-m">{job.score.toFixed(2)}</Typography>
          ) : (
            <Typography variant="paragraph-m" className="grey-text">
              -
            </Typography>
          ),
        farm: (
          <TooltipWrapper tooltip={{ title: job.farm.name, placement: "top" }}>
            <Typography variant="paragraph-m">{job.farm.name}</Typography>
          </TooltipWrapper>
        ),
        country: job.farm.country ? (
          <Typography variant="paragraph-m">{job.farm.country}</Typography>
        ) : (
          <Typography variant="paragraph-m" className="grey-text">
            -
          </Typography>
        ),
        triggeredBy: <DocScanTriggeredBy source={job.source} isAdmin={job.isAdmin} user={job.isAdmin ? job.adminUser : job.user} />,
        duration: durationContent ? (
          <Typography variant="paragraph-m">{durationContent}</Typography>
        ) : (
          <Typography variant="paragraph-m" className="grey-text">
            -
          </Typography>
        ),
        actions: (
          <Icon
            iconName="tabler/eye"
            className="icon inline pointer"
            color="green"
            onClick={() => {
              navigate(`/verifications/doc-scans/${job.id}`);
            }}
          />
        ),
      };
    }) ?? [];

  const handleCreateModalClose = () => {
    form.resetFields();
    setDocScanFile(null);
    setUploadState({
      success: false,
      uploading: false,
      filename: undefined,
      errorMessage: undefined,
    });
    setShowCreateModal(false);
  };

  const handleFileBeforeUpload = (file: File) => {
    const isPdf = file.type === "application/pdf";

    if (!isPdf) {
      setUploadState({
        success: false,
        uploading: false,
        filename: undefined,
        errorMessage: "Only PDF files are supported.",
      });
      setDocScanFile(null);
      return false;
    }

    if (file.size > DOC_SCAN_MAX_FILE_SIZE_BYTES) {
      setUploadState({
        success: false,
        uploading: false,
        filename: undefined,
        errorMessage: `The file is too large. Please upload a PDF under ${DOC_SCAN_MAX_FILE_SIZE_MB} MB.`,
      });
      setDocScanFile(null);
      return false;
    }

    setUploadState({
      success: true,
      uploading: false,
      filename: file.name,
      errorMessage: undefined,
    });
    setDocScanFile(file);

    return false;
  };

  const handleFileDelete = () => {
    setDocScanFile(null);
    setUploadState({
      success: false,
      uploading: false,
      filename: undefined,
      errorMessage: undefined,
    });
  };

  const { mutate: createJob, isPending: isCreating } = useMutation({
    mutationFn: createFarmDocumentScanFromDocument,
    onSuccess: () => {
      message({
        content: "Doc-scan job created successfully.",
        icon: "gremlin",
      });
      handleCreateModalClose();
      queryClient.invalidateQueries({ queryKey: [GET_DOC_SCANS] });
    },
    onError: () => {
      message({
        content: "The gremlins had trouble creating this doc-scan job. Please try again.",
        icon: "gremlin",
      });
    },
  });

  const handleCreateSubmit = ({ farmId, type }: { farmId: string; type: DocScanType }) => {
    if (!docScanFile) {
      setUploadState((prev) => ({
        ...prev,
        success: false,
        errorMessage: "Please upload a PDF file.",
      }));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      message({
        content: "The gremlins could not read your file. Please try again.",
        icon: "gremlin",
      });
    };

    reader.onload = async () => {
      const { result } = reader;

      if (typeof result !== "string" || !result) {
        message({
          content: "The gremlins could not process your file. Please try again.",
          icon: "gremlin",
        });
        return;
      }

      createJob({
        farmId,
        type,
        file: result,
        fileType: "application/pdf",
        fileName: docScanFile.name,
        fileHash: await hashFile(docScanFile),
      });
    };

    reader.readAsDataURL(docScanFile);
  };

  return (
    <MainPage loading={isLoading} pageName="admin">
      <AdminHead
        headline={`${data?.count ?? 0} doc-scan jobs`}
        description="View document scan jobs here. You can review their status and timing."
        extra={
          <Button type="primary" size="small" onClick={() => setShowCreateModal(true)}>
            Create
          </Button>
        }
      />
      <Modal
        wrapClassName="doc-scan-create-modal-wrap"
        showModal={showCreateModal}
        onCancel={handleCreateModalClose}
        closeIcon={<Icon iconName="tabler/x" />}
        loading={isCreating}
        title="Create doc-scan job"
        footer={
          <div className="doc-scan-create-footer">
            <Button type="secondary" onClick={handleCreateModalClose}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={isCreating}>
              Create
            </Button>
          </div>
        }
        content={
          <Form form={form} onFinish={handleCreateSubmit} className="doc-scan-create-form">
            <Row>
              <Col>
                <Input name="farmId" label="Farm ID" rules={[{ required: true }]} />
              </Col>
            </Row>
            <Row className="margin-top-200">
              <Col>
                <Select name="type" label="Type" rules={[{ required: true }]} optionLabelProp="label">
                  <Option value="extract" label={TYPE_LABELS.extract}>
                    <div style={{ padding: "4px 0" }}>
                      <div>
                        <Typography variant="paragraph-m">{TYPE_LABELS.extract}</Typography>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <Typography variant="paragraph-s" className="grey-text">
                          Extracts fields and crops from the uploaded document
                        </Typography>
                      </div>
                    </div>
                  </Option>
                  <Option value="match" label={TYPE_LABELS.match}>
                    <div style={{ padding: "4px 0" }}>
                      <div>
                        <Typography variant="paragraph-m">{TYPE_LABELS.match}</Typography>
                      </div>
                      <div style={{ marginTop: 4 }}>
                        <Typography variant="paragraph-s" className="grey-text">
                          Matches extracted document data against existing farm records
                        </Typography>
                      </div>
                    </div>
                  </Option>
                </Select>
              </Col>
            </Row>
            <Row className="margin-top-200">
              <Col>
                <AlertBanner
                  type="info"
                  iconName="tabler/info-circle"
                  iconSize={20}
                  descriptionSize="paragraph-s"
                  description="If this file was scanned in the last 7 days, the earlier scan results are reused. To scan it again, rename the file before you upload it."
                />
              </Col>
            </Row>
            <Row className="margin-top-200">
              <Col>
                <UploadFile
                  label="Upload PDF"
                  helpMessage="Upload a PDF document for this farm."
                  {...uploadState}
                  beforeUpload={handleFileBeforeUpload}
                  onDelete={handleFileDelete}
                />
              </Col>
            </Row>
          </Form>
        }
      />
      {globalStats && <DocScanStatisticsCard statistics={globalStats} totalFarms={globalStats.totalFarms} />}
      <Row>
        <Col>
          <Table<TableRow>
            rowKey="key"
            data={tableData}
            columns={columns}
            loading={isLoading}
            pagination={{
              total: data?.count,
              defaultPageSize: defaultLimit,
              pageSize: limit,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: handlePageChange,
            }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </MainPage>
  );
};
